"use client";

import { useEffect, useRef, useState, type ComponentType } from "react";
import type { AreaToggleSport } from "../area-view-types";
import { useAreaSport } from "./area-sport-state";
import type { AreaLiveMapCanvasProps, AreaLiveMapPoint } from "./AreaLiveMapCanvas";

export type AreaLiveMapLayer = Readonly<{ key: AreaToggleSport; points: readonly AreaLiveMapPoint[] }>;

/**
 * The map card's live map island. The server draws the numbered dots as an SVG under this layer
 * (the placeholder, and the fallback without JS or WebGL). Nothing heavy is in this file: once the
 * card comes within a screen of the viewport, it imports AreaLiveMapCanvas (MapLibre GL, its CSS
 * and the atlas style), and the map takes over the same box only once its first tiles are drawn,
 * so the page's first load and layout never change. Any failure leaves the drawing in place.
 */
export function AreaLiveMap({
  primary,
  layers,
  areaName,
}: Readonly<{ primary: AreaToggleSport; layers: readonly AreaLiveMapLayer[]; areaName: string }>) {
  const ref = useRef<HTMLDivElement>(null);
  const [Canvas, setCanvas] = useState<ComponentType<AreaLiveMapCanvasProps> | null>(null);
  const [state, setState] = useState<"idle" | "ready" | "failed">("idle");
  const sport = useAreaSport(primary, layers.map((layer) => layer.key));
  const layer = layers.find((candidate) => candidate.key === sport) ?? layers[0];

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    let cancelled = false;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      import("./AreaLiveMapCanvas")
        .then((module) => { if (!cancelled) setCanvas(() => module.AreaLiveMapCanvas); })
        .catch(() => { if (!cancelled) setState("failed"); });
    }, { rootMargin: "600px 0px" });
    observer.observe(node);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, []);

  const ready = state === "ready";
  return (
    <div
      ref={ref}
      data-area-live-map={state}
      className={`absolute inset-0 ${ready ? "" : "pointer-events-none opacity-0"}`}
      aria-hidden={ready ? undefined : true}
    >
      {Canvas && state !== "failed" && layer ? (
        <Canvas
          sport={layer.key}
          points={layer.points}
          areaName={areaName}
          onReady={() => setState("ready")}
          onFailure={() => setState("failed")}
        />
      ) : null}
    </div>
  );
}
